import { useReducer, useCallback, useEffect, useRef, useState } from "react";
import ImportPanel from "./VectorAnimator/ImportPanel";
import ElementsPanel from "./VectorAnimator/ElementsPanel";
import Canvas from "./VectorAnimator/Canvas";
import PropertiesPanel from "./VectorAnimator/PropertiesPanel";
import Timeline from "./VectorAnimator/Timeline/Timeline";
import ExportPanel from "./VectorAnimator/ExportPanel";
import VideoExportPanel from "./VectorAnimator/VideoExportPanel";
import BackgroundControl from "./VectorAnimator/BackgroundControl";
import { flattenTree, updateNodeInTree, createGroupNode, readSvgFile, CANVAS_ID } from "../utils/svgParser";
import { exportToCSS } from "../utils/exportCSS";
import { useAnimationEngine } from "../hooks/useAnimationEngine";

const initialState = {
  canvasSize: { width: 600, height: 600 },
  elementTree: [], // array of top-level group node, 1 per file SVG yang diimport
  selectedId: null,
  keyframeData: {},
  currentTime: 0,
  duration: 3,
  backgroundColor: "#ffffff", // null = transparan
};

function domainReducer(state, action) {
  switch (action.type) {
    // dipakai buat import pertama (layar penuh) MAUPUN import tambahan ("+ SVG").
    // tiap file dibungkus jadi 1 group node terpisah, biar beberapa SVG bisa hidup
    // berdampingan (mirip layer/folder) alih-alih saling menimpa.
    case "IMPORT_SVG": {
      const { tree, fileName } = action.payload;
      const groupNode = createGroupNode(fileName, tree);
      const isFirstImport = state.elementTree.length === 0;
      return {
        ...state,
        // import pertama juga nentuin ukuran default canvas dari dimensi SVG itu sendiri
        canvasSize: isFirstImport
          ? { width: action.payload.width, height: action.payload.height }
          : state.canvasSize,
        elementTree: [...state.elementTree, groupNode],
      };
    }

    case "SELECT_ELEMENT":
      return { ...state, selectedId: action.payload };

    case "UPDATE_TRANSFORM": {
      const { id, key, value } = action.payload;
      return {
        ...state,
        elementTree: updateNodeInTree(state.elementTree, id, (n) => ({
          ...n,
          transform: { ...n.transform, [key]: value },
        })),
      };
    }

    case "UPDATE_ATTR": {
      const { id, key, value } = action.payload;
      return {
        ...state,
        elementTree: updateNodeInTree(state.elementTree, id, (n) => ({
          ...n,
          attrs: { ...n.attrs, [key]: value },
        })),
      };
    }

    case "TOGGLE_VISIBILITY": {
      const { id } = action.payload;
      return {
        ...state,
        elementTree: updateNodeInTree(state.elementTree, id, (n) => ({
          ...n,
          hidden: !n.hidden,
        })),
      };
    }

    case "TOGGLE_LOCK": {
      const { id } = action.payload;
      return {
        ...state,
        elementTree: updateNodeInTree(state.elementTree, id, (n) => ({
          ...n,
          locked: !n.locked,
        })),
      };
    }

    case "ADD_KEYFRAME": {
      const { id, prop, time, value, easing = "power1.inOut" } = action.payload;
      const existing = state.keyframeData[id]?.[prop] || [];
      const filtered = existing.filter((kf) => kf.time !== time);
      const updated = [...filtered, { time, value, easing }].sort(
        (a, b) => a.time - b.time
      );
      return {
        ...state,
        keyframeData: {
          ...state.keyframeData,
          [id]: { ...state.keyframeData[id], [prop]: updated },
        },
      };
    }

    case "MOVE_KEYFRAME": {
      const { id, prop, oldTime, newTime } = action.payload;
      const existing = state.keyframeData[id]?.[prop] || [];
      const updated = existing
        .map((kf) => (kf.time === oldTime ? { ...kf, time: newTime } : kf))
        .sort((a, b) => a.time - b.time);
      return {
        ...state,
        keyframeData: {
          ...state.keyframeData,
          [id]: { ...state.keyframeData[id], [prop]: updated },
        },
      };
    }

    case "DELETE_KEYFRAME": {
      const { id, prop, time } = action.payload;
      const existing = state.keyframeData[id]?.[prop] || [];
      return {
        ...state,
        keyframeData: {
          ...state.keyframeData,
          [id]: {
            ...state.keyframeData[id],
            [prop]: existing.filter((kf) => kf.time !== time),
          },
        },
      };
    }

    case "SET_EASING": {
      const { id, prop, time, easing } = action.payload;
      const existing = state.keyframeData[id]?.[prop] || [];
      return {
        ...state,
        keyframeData: {
          ...state.keyframeData,
          [id]: {
            ...state.keyframeData[id],
            [prop]: existing.map((kf) => (kf.time === time ? { ...kf, easing } : kf)),
          },
        },
      };
    }

    case "SET_TIME":
      return { ...state, currentTime: action.payload };

    case "SET_DURATION":
      return { ...state, duration: action.payload };

    case "SET_BACKGROUND_COLOR":
      return { ...state, backgroundColor: action.payload };

    case "SET_CANVAS_SIZE":
      return { ...state, canvasSize: action.payload };

    default:
      return state;
  }
}

// ==================== UNDO / REDO ====================
// Action yang dianggap "edit beneran" dan layak masuk undo stack. SELECT_ELEMENT dan
// SET_TIME sengaja DIKELUARKAN — pilih elemen atau scrub playhead bukan "perubahan
// data" yang orang harap bisa di-undo, dan kalau ikut ke-track bakal bikin riwayat
// undo penuh noise (tiap klik/scrub jadi 1 step).
const UNDOABLE_ACTIONS = new Set([
  "IMPORT_SVG",
  "UPDATE_TRANSFORM",
  "UPDATE_ATTR",
  "TOGGLE_VISIBILITY",
  "TOGGLE_LOCK",
  "ADD_KEYFRAME",
  "MOVE_KEYFRAME",
  "DELETE_KEYFRAME",
  "SET_EASING",
  "SET_BACKGROUND_COLOR",
  "SET_CANVAS_SIZE",
  "SET_DURATION",
]);

const MAX_HISTORY = 60;

const initialHistory = {
  past: [],
  present: initialState,
  future: [],
  // "batching" dipakai buat gabungin banyak dispatch beruntun (misal drag resize yang
  // nembak UPDATE_TRANSFORM puluhan kali per detik) jadi SATU langkah undo, bukan
  // satu per pixel gerakan mouse. Komponen manggil BEGIN_BATCH di awal drag (mousedown)
  // dan END_BATCH di akhir (mouseup) — lihat onDragStart/onDragEnd yang dioper ke
  // Canvas, SelectionOverlay, dan Keyframe.
  batching: false,
  batchStart: null,
};

function historyReducer(history, action) {
  switch (action.type) {
    case "UNDO": {
      if (history.past.length === 0) return history;
      const previous = history.past[history.past.length - 1];
      return {
        past: history.past.slice(0, -1),
        present: previous,
        future: [history.present, ...history.future].slice(0, MAX_HISTORY),
        batching: false,
        batchStart: null,
      };
    }

    case "REDO": {
      if (history.future.length === 0) return history;
      const next = history.future[0];
      return {
        past: [...history.past, history.present].slice(-MAX_HISTORY),
        present: next,
        future: history.future.slice(1),
        batching: false,
        batchStart: null,
      };
    }

    case "BEGIN_BATCH": {
      if (history.batching) return history; // nested/duplicate begin, abaikan
      return { ...history, batching: true, batchStart: history.present };
    }

    case "END_BATCH": {
      if (!history.batching) return history;
      const changed = history.batchStart !== history.present;
      if (!changed) return { ...history, batching: false, batchStart: null };
      return {
        past: [...history.past, history.batchStart].slice(-MAX_HISTORY),
        present: history.present,
        future: [],
        batching: false,
        batchStart: null,
      };
    }

    default: {
      const newPresent = domainReducer(history.present, action);
      if (newPresent === history.present) return history; // gak ada perubahan nyata

      // selama batching (drag lagi jalan) ATAU action ini bukan tipe yang di-track,
      // cuma update present tanpa nge-push history baru
      if (history.batching || !UNDOABLE_ACTIONS.has(action.type)) {
        return { ...history, present: newPresent };
      }

      return {
        past: [...history.past, history.present].slice(-MAX_HISTORY),
        present: newPresent,
        future: [],
        batching: false,
        batchStart: null,
      };
    }
  }
}

export default function VectorAnimator() {
  const [history, dispatch] = useReducer(historyReducer, initialHistory);
  const state = history.present;
  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;
  const [showExport, setShowExport] = useState(false);
  const [showVideoExport, setShowVideoExport] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loop, setLoop] = useState(true);
  const rafRef = useRef(null);
  const lastTickRef = useRef(null);
  const svgRef = useRef(null);
  // ref terpisah buat nyimpen currentTime & duration "real-time" di dalam RAF loop,
  // supaya gak kena stale closure dari state React (lihat penjelasan di useEffect playback)
  const currentTimeRef = useRef(state.currentTime);
  const durationRef = useRef(state.duration);

  const engine = useAnimationEngine(state.keyframeData, state.duration);

  const handleImport = useCallback((parsed) => {
    dispatch({ type: "IMPORT_SVG", payload: parsed });
  }, []);

  // dipanggil dari tombol "+ SVG" di ElementsPanel — import tambahan (bukan yang pertama)
  const handleAddImport = useCallback((file) => {
    readSvgFile(file)
      .then((parsed) => dispatch({ type: "IMPORT_SVG", payload: parsed }))
      .catch((err) => {
        // MVP: tampilkan lewat alert biar gak nambah state/komponen baru cuma buat toast
        window.alert(err.message || "Gagal import SVG.");
      });
  }, []);

  // Handler ini di-useCallback dan diteruskan ke Canvas yang di-memo — penting supaya
  // referensinya stabil antar render, kalau tidak Canvas akan re-render tiap frame
  // playback dan menimpa balik animasi yang lagi dijalankan GSAP.
  const handleSelect = useCallback((id) => {
    dispatch({ type: "SELECT_ELEMENT", payload: id });
  }, []);

  // dipanggil dari drag handle resize di SelectionOverlay. id diteruskan eksplisit dari
  // pemanggil (bukan dibaca dari state.selectedId di closure) supaya handler ini tetap stabil.
  const handleScaleChange = useCallback((id, scaleX, scaleY) => {
    dispatch({ type: "UPDATE_TRANSFORM", payload: { id, key: "scaleX", value: scaleX } });
    dispatch({ type: "UPDATE_TRANSFORM", payload: { id, key: "scaleY", value: scaleY } });
  }, []);

  const handleRotateChange = useCallback((id, rotate) => {
    dispatch({ type: "UPDATE_TRANSFORM", payload: { id, key: "rotate", value: rotate } });
  }, []);

  // dipanggil dari drag-to-move langsung di elemen canvas (RenderNode.handleMouseDown)
  const handleMoveElement = useCallback((id, x, y) => {
    dispatch({ type: "UPDATE_TRANSFORM", payload: { id, key: "x", value: x } });
    dispatch({ type: "UPDATE_TRANSFORM", payload: { id, key: "y", value: y } });
  }, []);

  // dipanggil dari PropertiesPanel buat ubah fill/stroke elemen
  const handleAttrChange = useCallback((id, key, value) => {
    dispatch({ type: "UPDATE_ATTR", payload: { id, key, value } });
  }, []);

  // dipanggil dari tombol mata di ElementsPanel
  const handleToggleVisibility = useCallback((id) => {
    dispatch({ type: "TOGGLE_VISIBILITY", payload: { id } });
  }, []);

  // dipanggil dari tombol gembok di ElementsPanel
  const handleToggleLock = useCallback((id) => {
    dispatch({ type: "TOGGLE_LOCK", payload: { id } });
  }, []);

  const handleBackgroundChange = useCallback((color) => {
    dispatch({ type: "SET_BACKGROUND_COLOR", payload: color });
  }, []);

  const handleCanvasSizeChange = useCallback((width, height) => {
    dispatch({ type: "SET_CANVAS_SIZE", payload: { width, height } });
  }, []);

  // dipanggil di awal/akhir gesture drag manapun (resize, rotate, move, drag keyframe)
  // biar seluruh drag itu kesimpen sebagai SATU langkah undo, bukan satu per pixel gerakan
  const handleBeginBatch = useCallback(() => dispatch({ type: "BEGIN_BATCH" }), []);
  const handleEndBatch = useCallback(() => dispatch({ type: "END_BATCH" }), []);

  const handleUndo = useCallback(() => dispatch({ type: "UNDO" }), []);
  const handleRedo = useCallback(() => dispatch({ type: "REDO" }), []);

  const flatList = flattenTree(state.elementTree);
  const selectedNode = flatList.find((n) => n.id === state.selectedId);

  // sinkronisasi engine tiap kali currentTime berubah dari luar (drag playhead / keyframe)
  useEffect(() => {
    currentTimeRef.current = state.currentTime;
    engine.seek(state.currentTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentTime, state.keyframeData]);

  useEffect(() => {
    durationRef.current = state.duration;
  }, [state.duration]);

  // shortcut keyboard buat undo/redo — Ctrl/Cmd+Z buat undo, Ctrl/Cmd+Shift+Z atau
  // Ctrl+Y buat redo. Diabaikan kalau fokus lagi ada di input/textarea (misal lagi
  // ngetik angka di panel Properties) biar gak nyelonong nge-undo history browser
  // atau ganggu text selection normal di dalam field.
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      if (!isCtrlOrCmd) return;

      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      if (e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.key.toLowerCase() === "z" && e.shiftKey) || e.key.toLowerCase() === "y") {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleUndo, handleRedo]);

  // loop playback pakai requestAnimationFrame, biar playhead UI ikut jalan.
  // PENTING: tick() baca & tulis currentTimeRef/durationRef (ref), BUKAN state.currentTime/
  // state.duration langsung. Kalau baca dari state di sini, nilainya "beku" di momen efek ini
  // pertama dibuat (closure lama), sehingga waktu gak pernah nambah dengan benar dan playhead
  // nyangkut di angka kecil — itu penyebab tombol Play kelihatan gak jalan.
  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTickRef.current = null;
      return;
    }

    const tick = (now) => {
      if (lastTickRef.current == null) lastTickRef.current = now;
      const delta = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;

      let next = currentTimeRef.current + delta;
      const duration = durationRef.current;

      if (next >= duration) {
        if (loop) {
          next = 0;
        } else {
          currentTimeRef.current = duration;
          dispatch({ type: "SET_TIME", payload: duration });
          engine.seek(duration);
          setIsPlaying(false);
          return;
        }
      }

      currentTimeRef.current = next;
      dispatch({ type: "SET_TIME", payload: next });
      engine.seek(next);

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, loop]);

  const handleAddKeyframe = useCallback(
    (prop, value) => {
      if (!state.selectedId) return;
      dispatch({
        type: "ADD_KEYFRAME",
        payload: { id: state.selectedId, prop, time: state.currentTime, value },
      });
    },
    [state.selectedId, state.currentTime]
  );

  const exportedCSS = exportToCSS(state.keyframeData, state.duration);

  // Canvas.js masih pakai bentuk prop "svgMeta" {viewBox,width,height} — sekarang diturunkan
  // dari canvasSize (yang bisa diedit manual lewat panel Properties saat "Canvas" diselect),
  // bukan cuma dari dimensi SVG yang pertama kali diimport.
  const svgMeta = {
    viewBox: `0 0 ${state.canvasSize.width} ${state.canvasSize.height}`,
    width: state.canvasSize.width,
    height: state.canvasSize.height,
  };

  if (!state.elementTree.length) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <h1 className="text-[#D7C7A1] text-xl mb-1 text-center font-medium">
            Vector Animator
          </h1>
          <p className="text-[#D7C7A1]/50 text-sm text-center mb-6">
            Import SVG, animasikan, export ke CSS.
          </p>
          <ImportPanel onImport={handleImport} />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#0F0F0F] flex flex-col">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#145A43]/40 shrink-0">
        <h1 className="text-[#D7C7A1] text-sm font-medium">Vector Animator</h1>
        <BackgroundControl
          backgroundColor={state.backgroundColor}
          onChange={handleBackgroundChange}
        />
        <div className="flex gap-2">
          <button
            onClick={handleUndo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            className="text-xs px-3 py-1.5 rounded bg-[#145A43] text-[#D7C7A1] hover:bg-[#2F8F6F] hover:text-[#0F0F0F] transition-colors disabled:opacity-30 disabled:hover:bg-[#145A43] disabled:hover:text-[#D7C7A1] disabled:cursor-not-allowed"
          >
            ↶ Undo
          </button>
          <button
            onClick={handleRedo}
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
            className="text-xs px-3 py-1.5 rounded bg-[#145A43] text-[#D7C7A1] hover:bg-[#2F8F6F] hover:text-[#0F0F0F] transition-colors disabled:opacity-30 disabled:hover:bg-[#145A43] disabled:hover:text-[#D7C7A1] disabled:cursor-not-allowed"
          >
            ↷ Redo
          </button>
          <button
            onClick={() => setShowExport(true)}
            className="text-xs px-3 py-1.5 rounded bg-[#145A43] text-[#D7C7A1] hover:bg-[#2F8F6F] hover:text-[#0F0F0F] transition-colors"
          >
            Export CSS
          </button>
          <button
            onClick={() => setShowVideoExport(true)}
            className="text-xs px-3 py-1.5 rounded bg-[#2F8F6F] text-[#0F0F0F] font-medium hover:bg-[#3aa87f] transition-colors"
          >
            Export Video
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <ElementsPanel
          tree={state.elementTree}
          selectedId={state.selectedId}
          onSelect={handleSelect}
          onToggleVisibility={handleToggleVisibility}
          onToggleLock={handleToggleLock}
          onAddImport={handleAddImport}
        />
        <Canvas
          ref={svgRef}
          svgMeta={svgMeta}
          elementTree={state.elementTree}
          selectedId={state.selectedId}
          onSelect={handleSelect}
          onMoveElement={handleMoveElement}
          backgroundColor={state.backgroundColor}
          isPlaying={isPlaying}
          onScaleChange={handleScaleChange}
          onRotateChange={handleRotateChange}
          onDragStart={handleBeginBatch}
          onDragEnd={handleEndBatch}
        />
        <PropertiesPanel
          selectedId={state.selectedId}
          elementTree={state.elementTree}
          onChange={(key, value) =>
            dispatch({
              type: "UPDATE_TRANSFORM",
              payload: { id: state.selectedId, key, value },
            })
          }
          onAttrChange={handleAttrChange}
          onAddKeyframe={handleAddKeyframe}
          canvasSize={state.canvasSize}
          backgroundColor={state.backgroundColor}
          onCanvasSizeChange={handleCanvasSizeChange}
          onCanvasBackgroundChange={handleBackgroundChange}
          onBeginBatch={handleBeginBatch}
          onEndBatch={handleEndBatch}
        />
      </div>

      <Timeline
        selectedId={state.selectedId === CANVAS_ID ? null : state.selectedId}
        selectedLabel={selectedNode?.name || selectedNode?.id}
        keyframeData={state.keyframeData}
        duration={state.duration}
        currentTime={state.currentTime}
        isPlaying={isPlaying}
        loop={loop}
        onToggleLoop={() => setLoop((l) => !l)}
        onSeek={(t) => dispatch({ type: "SET_TIME", payload: Math.min(t, state.duration) })}
        onPlayPause={() => setIsPlaying((p) => !p)}
        onDurationChange={(d) => dispatch({ type: "SET_DURATION", payload: d })}
        onMoveKeyframe={(id, prop, oldTime, newTime) =>
          dispatch({ type: "MOVE_KEYFRAME", payload: { id, prop, oldTime, newTime } })
        }
        onDeleteKeyframe={(id, prop, time) =>
          dispatch({ type: "DELETE_KEYFRAME", payload: { id, prop, time } })
        }
        onEasingChange={(id, prop, time, easing) =>
          dispatch({ type: "SET_EASING", payload: { id, prop, time, easing } })
        }
        onDragStart={handleBeginBatch}
        onDragEnd={handleEndBatch}
      />

      {showExport && (
        <ExportPanel css={exportedCSS} onClose={() => setShowExport(false)} />
      )}

      {showVideoExport && (
        <VideoExportPanel
          svgRef={svgRef}
          engine={engine}
          duration={state.duration}
          backgroundColor={state.backgroundColor}
          onClose={() => setShowVideoExport(false)}
          onDone={() => setIsPlaying(false)}
        />
      )}
    </div>
  );
}
